
import { createAction, createSlice } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';

import type { OrgUnitNode } from './../lib/types';
import { orgUnitApi } from './../lib/api';
import type { AppState } from './redux';

export const ADD_ORG_UNIT = 'vehicles/org-units/add';
export const UPDATE_ORG_UNIT = 'vehicles/org-units/update';
export const DELETE_ORG_UNIT = 'vehicles/org-units/delete';
export const SET_ORG_UNITS = 'vehicles/org-units/set';

const initialState: OrgUnitNode | null = null;

export const replaceOrgUnitInTree = (
  node: OrgUnitNode,
  orgUnit: OrgUnitNode
) => {
  let currentNode = { ...node };
  if (node.id === orgUnit.id) {
    currentNode = {
      ...currentNode,
      ...orgUnit
    };
  }
  let children = currentNode.children ? [...currentNode.children] : [];
  children = children.map<OrgUnitNode>(child =>
    replaceOrgUnitInTree(child, orgUnit)
  );
  return {
    ...currentNode,
    children: children.length ? children : null
  };
};

const addOrgUnitToTree = (
  node: OrgUnitNode,
  parentId: number,
  orgUnit: OrgUnitNode
) => {
  let children = node.children ? [...node.children] : [];
  children = children.map(child => addOrgUnitToTree(child, parentId, orgUnit));
  if (node.id === parentId) {
    children = [...children, orgUnit];
  }
  return {
    ...node,
    children: children.length ? children : null
  };
};

const removeFromTree = (node: OrgUnitNode, orgUnitId: number) => {
  let children = node.children ? [...node.children] : [];
  children = children
    .filter(child => child.id !== orgUnitId)
    .map(child => removeFromTree(child, orgUnitId));
  return {
    ...node,
    children: children.length ? children : null
  };
};

const setOrgUnitsAction = createAction<OrgUnitNode>(SET_ORG_UNITS);

const orgUnitsSlice = createSlice({
  name: 'orgUnits',
  initialState: initialState as OrgUnitNode | null,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(setOrgUnitsAction, (state, { payload }) => payload);
  }
});

export const orgUnitsActions = {
  set: setOrgUnitsAction
};

export const addOrgUnit = (
  orgUnit: OrgUnitNode,
  parentOrgUnitId: number
 ) => async (
  dispatch: Dispatch,
  getState: () => AppState
): Promise<void> => {
  const addedOrgUnit = await orgUnitApi.addOrgUnit({
    ...orgUnit,
    parentId: parentOrgUnitId
  });
  const state = getState();
  const orgUnits = addOrgUnitToTree(
    state.orgUnits as OrgUnitNode,
    parentOrgUnitId,
    addedOrgUnit
  );
  dispatch(orgUnitsActions.set({ ...orgUnits }));
};

export const updateOrgUnit = (orgUnit: OrgUnitNode) => async (
  dispatch: Dispatch,
  getState: () => AppState
): Promise<void> => {
  const updatedOrgUnit = await orgUnitApi.updateOrgUnit(orgUnit);
  const state = getState();
  const orgUnits = replaceOrgUnitInTree(
    state.orgUnits as OrgUnitNode,
    updatedOrgUnit
  );
  dispatch(orgUnitsActions.set({ ...orgUnits }));
};

export const deleteOrgUnit = (id: number) => async (
  dispatch: Dispatch,
  getState: () => AppState
): Promise<void> => {
  await orgUnitApi.deleteOrgUnit(id);
  const state = getState();
  const orgUnits = removeFromTree(state.orgUnits as OrgUnitNode, id);
  dispatch(orgUnitsActions.set({ ...orgUnits }));
};

export const fetchOrgUnits = () => async (
  dispatch: Dispatch
): Promise<void> => {
  const orgUnits = await orgUnitApi.fetchOrgUnits();
  dispatch(orgUnitsActions.set({ ...orgUnits }));
};

export default orgUnitsSlice.reducer;
